import { lazy, Suspense, ReactNode, useEffect } from 'react';
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { RevenueCatProvider } from "@/contexts/RevenueCatContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { CoachStreamProvider } from "@/contexts/CoachStreamContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { AppLayout } from "@/components/layout/AppLayout";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { PushPromptHost } from "@/components/notifications/PushPromptHost";
import { SplashScreen } from "@/components/SplashScreen";
import { useProfile } from "@/hooks/useUserQueries";
import { useActiveProgram } from "@/hooks/useActiveProgram";
import { useHealthSync } from "@/hooks/useHealthSync";
import { supabase } from "@/integrations/supabase/client";
import { initDeepLinkHandler } from "@/lib/deepLinks";


// Lazy load pages for code splitting
const FoodTracker = lazy(() => import("@/pages/FoodTracker"));
const FoodScanner = lazy(() => import("@/pages/FoodScanner"));
const MealsPage = lazy(() => import("@/pages/MealsPage"));
const WorkoutTracker = lazy(() => import("@/pages/WorkoutTracker"));

const WorkoutSummary = lazy(() => import("@/pages/WorkoutSummary"));
const HyroxSummary = lazy(() => import("@/pages/HyroxSummary"));

const ActiveWorkout = lazy(() => import("@/pages/ActiveWorkout"));
const SavedRoutines = lazy(() => import("@/pages/SavedRoutines"));
const CreateRoutine = lazy(() => import("@/pages/CreateRoutine"));
const EditRoutine = lazy(() => import("@/pages/EditRoutine"));
const HyroxSetup = lazy(() => import("@/pages/HyroxSetup"));

const Coach = lazy(() => import("@/pages/Coach"));
const Transformation = lazy(() => import("@/pages/Transformation"));
const Profile = lazy(() => import("@/pages/Profile"));
const BaselineSetup = lazy(() => import("@/pages/BaselineSetup"));
const Programme = lazy(() => import("@/pages/Programme"));
const ProgrammeComplete = lazy(() => import("@/pages/ProgrammeComplete"));
const Finale = lazy(() => import("@/pages/Finale"));
const FinaleShare = lazy(() => import("@/pages/FinaleShare"));
const FinaleFork = lazy(() => import("@/pages/FinaleFork"));
const EditProgramme = lazy(() => import("@/pages/EditProgramme"));
const EditProgrammeCalendar = lazy(() => import("@/pages/EditProgrammeCalendar"));
const EditProgrammeMacros = lazy(() => import("@/pages/EditProgrammeMacros"));
const Settings = lazy(() => import("@/pages/Settings"));
const HealthScoreDetail = lazy(() => import("@/pages/HealthScoreDetail"));
const BodyAnalysis = lazy(() => import("@/pages/BodyAnalysis"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const V3Hook = lazy(() => import("@/pages/onboarding/v3/HookScreen"));
const V3Goal = lazy(() => import("@/pages/onboarding/v3/GoalScreen"));
const V3Name = lazy(() => import("@/pages/onboarding/v3/NameScreen"));
const V3Activity = lazy(() => import("@/pages/onboarding/v3/ActivityScreen"));
const V3Focus = lazy(() => import("@/pages/onboarding/v3/FocusScreen"));
const V3Days = lazy(() => import("@/pages/onboarding/v3/DaysScreen"));
const V3SessionLength = lazy(() => import("@/pages/onboarding/v3/SessionLengthScreen"));
const V3Experience = lazy(() => import("@/pages/onboarding/v3/ExperienceScreen"));
const V3Equipment = lazy(() => import("@/pages/onboarding/v3/EquipmentScreen"));
const V3You = lazy(() => import("@/pages/onboarding/v3/YouScreen"));
const V3Target = lazy(() => import("@/pages/onboarding/v3/TargetScreen"));
const V3Injuries = lazy(() => import("@/pages/onboarding/v3/InjuriesScreen"));
const V3Extras = lazy(() => import("@/pages/onboarding/v3/ExtrasScreen"));
const V3Scan = lazy(() => import("@/pages/onboarding/v3/ScanScreen"));
const V3Save = lazy(() => import("@/pages/onboarding/v3/SaveScreen"));
const V3Building = lazy(() => import("@/pages/onboarding/v3/BuildingScreen"));
const V3Delivery = lazy(() => import("@/pages/onboarding/v3/DeliveryScreen"));
const V3Rating = lazy(() => import("@/pages/onboarding/v3/RatingScreen"));
const V3Notifications = lazy(() => import("@/pages/onboarding/v3/NotificationsScreen"));

const Auth = lazy(() => import("@/pages/Auth"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));

const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Contact = lazy(() => import("@/pages/Contact"));
const OAuthConsent = lazy(() => import("@/pages/OAuthConsent"));

const Subscription = lazy(() => import("@/pages/Subscription"));


const NotFound = lazy(() => import("@/pages/NotFound"));
const AddProgressPhoto = lazy(() => import("@/pages/AddProgressPhoto"));
const AddBodyMeasurement = lazy(() => import("@/pages/AddBodyMeasurement"));
const WeeklyCheckin = lazy(() => import("@/pages/WeeklyCheckin"));
const WeeklyRecap = lazy(() => import("@/pages/WeeklyRecap"));
const Journey = lazy(() => import("@/pages/Journey"));

// Wrapper to add error boundary to each route
function WithErrorBoundary({ children }: { children: ReactNode }) {
  return <RouteErrorBoundary>{children}</RouteErrorBoundary>;
}

import { queryClient } from '@/lib/queryClient';


// Solid black fallback for the outer Suspense — chunk loads should never
// flash white. Per-route Suspense lives inside PageTransition so AppLayout
// (nav + banner) stays mounted while inner pages swap.
function PageLoader() {
  return <div style={{ background: '#000', minHeight: '100vh' }} />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  // Don't redirect while auth is still resolving — prevents
  // <Navigate> from firing replaceState on every transient render.
  if (loading) return null;
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

/**
 * Gates the main app layout on two conditions:
 *   1. `profiles.onboarding_complete === true` — freshly-signed-up users get
 *      routed to /onboarding.
 *   2. An active (or same-day completed) coach programme exists — users whose
 *      profile flag is set but who have no programme (legacy accounts, deleted
 *      programmes, post-completion gaps) get routed to /programme so they can
 *      hit "Build my programme" instead of wandering the main tabs with no
 *      plan and starting/cancelling empty workouts.
 *
 * Never renders a protected page before both signals resolve — that would
 * flash Dashboard for a beat and then redirect.
 */
function OnboardingGate({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { data: profile, isLoading: profileLoading, isFetching: profileFetching } = useProfile();
  const {
    data: activeProgram,
    isLoading: programLoading,
    isFetching: programFetching,
  } = useActiveProgram();

  if (authLoading) return <SplashScreen isLoading />;
  if (!user) return <Navigate to="/auth" replace />;
  if (profileLoading || (!profile && profileFetching)) {
    return <SplashScreen isLoading />;
  }
  if (!profile || (profile as any).onboarding_complete !== true) {
    let resumeOnboarding = false;
    try { resumeOnboarding = sessionStorage.getItem('onboarding_save_intent') === '1'; } catch { /* noop */ }
    return <Navigate to={resumeOnboarding ? '/onboarding/building?commit=1' : '/onboarding'} replace />;
  }
  // Wait for the programme query before deciding — otherwise a user with a
  // valid active programme would briefly redirect to /programme on cold load.
  if (programLoading || (activeProgram === undefined && programFetching)) {
    return <SplashScreen isLoading />;
  }
  if (!activeProgram) {
    return <Navigate to="/programme" replace />;
  }
  return <>{children}</>;
}



function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;
  if (user) {
    // Honour ?next= so the MCP OAuth consent flow (and any other deep link)
    // can bounce the user back to where they came from after sign-in. Only
    // same-origin relative paths are accepted.
    const params = new URLSearchParams(location.search);
    const next = params.get("next");
    const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
    return <Navigate to={safeNext} replace />;
  }

  return <>{children}</>;
}

const APP_VERSION = '2.3';

function AppRoutes() {
  const { loading, user } = useAuth();
  const navigate = useNavigate();
  const { data: profile } = useProfile();
  // Foreground HealthKit sync — runs only on iOS native when available.
  // `connect()` requests the FULL read set (CORE_HEALTH_SCOPES + vo2Max) in one prompt.
  const { isAvailable: healthAvailable, connect: connectHealth } = useHealthSync();

  // Listen for password-reset deep links so the iOS app never bounces to web.
  useEffect(() => {
    const cleanup = initDeepLinkHandler((path) => navigate(path));
    return cleanup;
  }, [navigate]);



  // One-time launch hygiene: clear orphaned active workout (>4h) and track app version.
  // Wrapped in try/catch — localStorage may be unavailable or corrupted.
  useEffect(() => {
    try {
      const raw = localStorage.getItem('active_workout');
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          const startTime = parsed?.startedAt ? new Date(parsed.startedAt).getTime() : 0;
          const fourHoursAgo = Date.now() - 4 * 60 * 60 * 1000;
          if (!startTime || startTime < fourHoursAgo) {
            localStorage.removeItem('active_workout');
          }
        } catch {
          localStorage.removeItem('active_workout');
        }
      }
    } catch {
      // localStorage unavailable — non-fatal
    }

    try {
      const lastVersion = localStorage.getItem('app_version');
      if (lastVersion !== APP_VERSION) {
        localStorage.setItem('app_version', APP_VERSION);
      }
    } catch {
      // Non-fatal
    }
  }, []);

  // Ask once for HealthKit permission after the user is signed in and onboarded.
  // Respects `profiles.health_prompt_dismissed_at` so fresh installs don't re-prompt
  // users who previously skipped. Silent failure — never blocks the user.
  useEffect(() => {
    if (!user || !healthAvailable) return;
    if (!profile || (profile as any).onboarding_complete !== true) return;
    if ((profile as any).health_prompt_dismissed_at) return;
    if (localStorage.getItem('healthkit_asked') === 'true') return;
    (async () => {
      await connectHealth();
      try {
        await supabase
          .from('profiles')
          .update({ health_prompt_dismissed_at: new Date().toISOString() } as any)
          .eq('id', user.id);
      } catch {
        // Non-fatal — localStorage flag still gates the in-session prompt.
      }
    })();
  }, [user, profile, healthAvailable, connectHealth]);

  // Track last_active_at on the user's profile so behavior-based notifications
  // (smart-notifications edge function) can detect lapsed users.
  useEffect(() => {
    if (!user) return;
    const updateLastActive = async () => {
      try {
        await supabase
          .from('profiles')
          .update({ last_active_at: new Date().toISOString() } as any)
          .eq('id', user.id);
      } catch {
        // Silent — never block the user
      }
    };
    void updateLastActive();
    const handleVisibility = () => {
      if (!document.hidden) void updateLastActive();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [user]);

  return (
    <>
      <SplashScreen isLoading={loading} />
      <ScrollToTop />
      <PushPromptHost />

      <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route
            path="/auth"
            element={
              <PublicRoute>
                <WithErrorBoundary><Auth /></WithErrorBoundary>
              </PublicRoute>
            }
          />
          <Route path="/auth/callback" element={<WithErrorBoundary><AuthCallback /></WithErrorBoundary>} />
          <Route path="/reset-password" element={<WithErrorBoundary><ResetPassword /></WithErrorBoundary>} />

          <Route path="/terms" element={<WithErrorBoundary><Terms /></WithErrorBoundary>} />
          <Route path="/privacy" element={<WithErrorBoundary><Privacy /></WithErrorBoundary>} />
          <Route path="/contact" element={<WithErrorBoundary><Contact /></WithErrorBoundary>} />
          <Route path="/.lovable/oauth/consent" element={<WithErrorBoundary><OAuthConsent /></WithErrorBoundary>} />
          
          {/* Onboarding v3: unauthenticated until /onboarding/save, which
              creates the account. Building onwards runs authenticated. */}
          <Route path="/onboarding" element={<WithErrorBoundary><Onboarding /></WithErrorBoundary>} />
          <Route path="/onboarding/hook" element={<WithErrorBoundary><V3Hook /></WithErrorBoundary>} />
          <Route path="/onboarding/goal" element={<WithErrorBoundary><V3Goal /></WithErrorBoundary>} />
          <Route path="/onboarding/name" element={<WithErrorBoundary><V3Name /></WithErrorBoundary>} />
          <Route path="/onboarding/activity" element={<WithErrorBoundary><V3Activity /></WithErrorBoundary>} />
          <Route path="/onboarding/focus" element={<WithErrorBoundary><V3Focus /></WithErrorBoundary>} />
          <Route path="/onboarding/days" element={<WithErrorBoundary><V3Days /></WithErrorBoundary>} />
          <Route path="/onboarding/session-length" element={<WithErrorBoundary><V3SessionLength /></WithErrorBoundary>} />
          <Route path="/onboarding/experience" element={<WithErrorBoundary><V3Experience /></WithErrorBoundary>} />
          <Route path="/onboarding/equipment" element={<WithErrorBoundary><V3Equipment /></WithErrorBoundary>} />
          <Route path="/onboarding/you" element={<WithErrorBoundary><V3You /></WithErrorBoundary>} />
          <Route path="/onboarding/target" element={<WithErrorBoundary><V3Target /></WithErrorBoundary>} />
          <Route path="/onboarding/injuries" element={<WithErrorBoundary><V3Injuries /></WithErrorBoundary>} />
          <Route path="/onboarding/extras" element={<WithErrorBoundary><V3Extras /></WithErrorBoundary>} />
          <Route path="/onboarding/scan" element={<WithErrorBoundary><V3Scan /></WithErrorBoundary>} />
          <Route path="/onboarding/save" element={<WithErrorBoundary><V3Save /></WithErrorBoundary>} />
          <Route path="/onboarding/building" element={<WithErrorBoundary><V3Building /></WithErrorBoundary>} />
          <Route path="/onboarding/delivery" element={<WithErrorBoundary><V3Delivery /></WithErrorBoundary>} />
          <Route path="/onboarding/rating" element={<WithErrorBoundary><V3Rating /></WithErrorBoundary>} />
          <Route path="/onboarding/notifications" element={<WithErrorBoundary><V3Notifications /></WithErrorBoundary>} />
          {/* Superseded v2 paths kept as redirects so old links never 404. */}
          <Route path="/onboarding/basics" element={<Navigate to="/onboarding/you" replace />} />
          <Route path="/onboarding/photos" element={<Navigate to="/onboarding/scan" replace />} />
          <Route path="/onboarding/reveal" element={<Navigate to="/onboarding/building" replace />} />
          <Route path="/onboarding/account" element={<Navigate to="/onboarding/save" replace />} />

          <Route
            path="/programme/welcome"

            element={
              <ProtectedRoute>
                <WithErrorBoundary><Programme fullscreen /></WithErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/programme"
            element={
              <ProtectedRoute>
                <WithErrorBoundary><Programme /></WithErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/programme/edit"
            element={
              <ProtectedRoute>
                <WithErrorBoundary><EditProgramme /></WithErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/programme/edit/calendar"
            element={
              <ProtectedRoute>
                <WithErrorBoundary><EditProgrammeCalendar /></WithErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/programme/edit/macros"
            element={
              <ProtectedRoute>
                <WithErrorBoundary><EditProgrammeMacros /></WithErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/finale/:programId"
            element={
              <ProtectedRoute>
                <WithErrorBoundary><Finale /></WithErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/finale/:programId/share"
            element={
              <ProtectedRoute>
                <WithErrorBoundary><FinaleShare /></WithErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/finale/:programId/fork"
            element={
              <ProtectedRoute>
                <WithErrorBoundary><FinaleFork /></WithErrorBoundary>
              </ProtectedRoute>
            }
          />

          <Route
            path="/programme/complete"
            element={
              <ProtectedRoute>
                <WithErrorBoundary><ProgrammeComplete /></WithErrorBoundary>
              </ProtectedRoute>
            }
          />




          <Route
            element={
              <ProtectedRoute>
                <OnboardingGate>
                  <AppLayout />
                </OnboardingGate>
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<WithErrorBoundary><Profile /></WithErrorBoundary>} />
            <Route path="/progress" element={<WithErrorBoundary><Transformation /></WithErrorBoundary>} />
            <Route path="/baseline-setup" element={<WithErrorBoundary><BaselineSetup /></WithErrorBoundary>} />
            <Route path="/food" element={<WithErrorBoundary><FoodTracker /></WithErrorBoundary>} />
            <Route path="/meals" element={<WithErrorBoundary><MealsPage /></WithErrorBoundary>} />
            <Route path="/workout" element={<WithErrorBoundary><WorkoutTracker /></WithErrorBoundary>} />
            
           <Route path="/workout/summary" element={<WithErrorBoundary><WorkoutSummary /></WithErrorBoundary>} />
           <Route path="/workout/hyrox-summary" element={<WithErrorBoundary><HyroxSummary /></WithErrorBoundary>} />
            
            <Route path="/workout/active" element={<WithErrorBoundary><ActiveWorkout /></WithErrorBoundary>} />
            <Route path="/workout/routines" element={<WithErrorBoundary><SavedRoutines /></WithErrorBoundary>} />
            <Route path="/workout/create-routine" element={<WithErrorBoundary><CreateRoutine /></WithErrorBoundary>} />
            <Route path="/workout/edit-routine/:id" element={<WithErrorBoundary><EditRoutine /></WithErrorBoundary>} />
            <Route path="/workout/hyrox-setup" element={<WithErrorBoundary><HyroxSetup /></WithErrorBoundary>} />
            
            <Route path="/coach" element={<WithErrorBoundary><Coach /></WithErrorBoundary>} />
            <Route path="/settings" element={<WithErrorBoundary><Settings /></WithErrorBoundary>} />
            <Route path="/health-score" element={<ProtectedRoute><WithErrorBoundary><HealthScoreDetail /></WithErrorBoundary></ProtectedRoute>} />

            <Route path="/body-analysis" element={<ProtectedRoute><WithErrorBoundary><BodyAnalysis /></WithErrorBoundary></ProtectedRoute>} />
          </Route>
          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <WithErrorBoundary><Subscription /></WithErrorBoundary>
              </ProtectedRoute>
            }
          />

          <Route
            path="/progress/photos/add"
            element={
              <ProtectedRoute>
                <WithErrorBoundary><AddProgressPhoto /></WithErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/progress/measurements/add"
            element={
              <ProtectedRoute>
                <WithErrorBoundary><AddBodyMeasurement /></WithErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/weekly-checkin"
            element={
              <ProtectedRoute>
                <WithErrorBoundary><WeeklyCheckin /></WithErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/weekly-recap"
            element={
              <ProtectedRoute>
                <WithErrorBoundary><WeeklyRecap /></WithErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/journey"
            element={
              <ProtectedRoute>
                <WithErrorBoundary><Journey /></WithErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/food/scan"
            element={
              <ProtectedRoute>
                <WithErrorBoundary><FoodScanner /></WithErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<WithErrorBoundary><NotFound /></WithErrorBoundary>} />
          </Routes>
      </Suspense>
    </>
  );
}

const App = () => (
  <ErrorBoundary>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <div>
            <OfflineIndicator />
            <Sonner />
            <BrowserRouter>
              <AuthProvider>
                <RevenueCatProvider>
                  <CoachStreamProvider>
                    <AppRoutes />
                  </CoachStreamProvider>
                </RevenueCatProvider>
              </AuthProvider>
            </BrowserRouter>
          </div>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
